import React, { useState, useEffect, useRef, useCallback } from "react";
import { Bell, Check, MessageCircle, Trophy, XCircle, Sparkles, ArrowRight, ClipboardList } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useWS } from "../../context/WebSocketContext";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const PAGE_SIZE = 30;

const ICON_MAP = {
  new: { icon: Sparkles, color: "text-[#25D366]", bg: "bg-[#25D366]/10" },
  message: { icon: MessageCircle, color: "text-blue-400", bg: "bg-blue-500/10" },
  won: { icon: Trophy, color: "text-[#25D366]", bg: "bg-[#25D366]/10" },
  lost: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/10" },
  moved: { icon: ArrowRight, color: "text-purple-400", bg: "bg-purple-500/10" },
  task: { icon: ClipboardList, color: "text-amber-400", bg: "bg-amber-500/10" },
};

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (new Date() - d) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

export default function NotificationsDropdown({ onOpenLead }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [activeTab, setActiveTab] = useState("unread");
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pulse, setPulse] = useState(false);
  const panelRef = useRef(null);
  const { on } = useWS();
  const { user } = useAuth();

  const fetchUnreadCount = useCallback(async () => {
    const { data } = await api.get("/notifications/unread-count");
    setUnread(data.count || 0);
    return data.count || 0;
  }, []);

  const fetchNotifications = useCallback(async ({ tab = activeTab, append = false } = {}) => {
    try {
      setLoading(true);
      const offset = append ? items.length : 0;
      const readFilter = tab === "unread" ? "&read=false" : "";
      const [list, unreadCount] = await Promise.all([
        api.get(`/notifications?limit=${PAGE_SIZE}&skip=${offset}${readFilter}`),
        fetchUnreadCount(),
      ]);
      const nextItems = Array.isArray(list.data) ? list.data : [];
      setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
      setHasMore(tab === "unread" ? offset + nextItems.length < unreadCount : nextItems.length === PAGE_SIZE);
    } catch {
      if (!append) setItems([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [activeTab, fetchUnreadCount, items.length]);

  useEffect(() => { fetchNotifications({ tab: "unread" }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const unsub = on("notification.created", (payload) => {
      if (!payload?.notification) return;
      const recipientId = payload.notification.recipient_user_id || payload.notification.user_id;
      if (recipientId && recipientId !== user?.id) return;

      setItems((prev) => {
        if (activeTab === "unread" && payload.notification.read) return prev;
        return [payload.notification, ...prev];
      });
      setUnread((c) => c + 1);
      setPulse(true);
      setTimeout(() => setPulse(false), 700);
    });
    return () => unsub?.();
  }, [activeTab, on, user?.id]);

  useEffect(() => {
    const h = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleOpenToggle = () => {
    setOpen((current) => {
      const nextOpen = !current;
      if (nextOpen) {
        setActiveTab("unread");
        fetchNotifications({ tab: "unread" });
      }
      return nextOpen;
    });
  };

  const switchTab = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    fetchNotifications({ tab });
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setUnread(0);
    setHasMore(false);
    if (activeTab === "unread") {
      setItems([]);
      return;
    }
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification?.read && notification?.id) {
        await api.post(`/notifications/${notification?.id}/read`);
        setUnread((count) => Math.max(0, count - 1));
        setItems((prev) => {
          if (activeTab === "unread") return prev.filter((n) => n.id !== notification?.id);
          return prev.map((n) => (n.id === notification?.id ? { ...n, read: true } : n));
        });
      }

      if (notification?.lead_id && onOpenLead) {
        const { data } = await api.get(`/leads/${notification?.lead_id}`);
        onOpenLead(data);
      }
    } catch {}

    setOpen(false);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        data-testid="notifications-btn"
        onClick={handleOpenToggle}
        className="relative p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded-lg transition"
      >
        <motion.span
          animate={pulse ? { scale: [1, 1.3, 1], rotate: [0, -15, 15, 0] } : {}}
          transition={{ duration: 0.6 }}
          className="inline-block"
        >
          <Bell className="w-5 h-5" />
        </motion.span>
        {unread > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#25D366] text-slate-950 text-[10px] font-bold flex items-center justify-center ring-2 ring-slate-900"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-96 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden"
            data-testid="notifications-panel"
          >
            <div className="px-4 py-3 border-b border-slate-800">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-bold text-slate-50" style={{ fontFamily: "Outfit" }}>Notificações</div>
                  <div className="text-[11px] text-slate-500">{unread} não lida{unread === 1 ? "" : "s"}</div>
                </div>
                {(items.length > 0 || unread > 0) && (
                  <button
                    data-testid="notifications-mark-all"
                    onClick={markAll}
                    className="text-[11px] font-semibold text-[#25D366] hover:text-[#1EAC52] flex items-center gap-1"
                  >
                    <Check className="w-3 h-3" /> Marcar todas como lidas
                  </button>
                )}
              </div>

              <div className="mt-3 grid grid-cols-2 gap-1 rounded-lg bg-slate-950/50 p-1">
                <button
                  type="button"
                  onClick={() => switchTab("unread")}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${activeTab === "unread" ? "bg-slate-800 text-slate-50" : "text-slate-400 hover:text-slate-200"}`}
                >
                  Não lidas ({unread})
                </button>
                <button
                  type="button"
                  onClick={() => switchTab("all")}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${activeTab === "all" ? "bg-slate-800 text-slate-50" : "text-slate-400 hover:text-slate-200"}`}
                >
                  Todas
                </button>
              </div>
            </div>

            <div className="max-h-[70vh] overflow-y-auto">
              {items.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-500">
                  <Bell className="w-10 h-10 mx-auto mb-2 text-slate-700" />
                  {loading ? "Carregando notificações..." : activeTab === "unread" ? "Nenhuma notificação não lida" : "Nenhuma notificação ainda"}
                </div>
              ) : (
                items.map((n) => {
                  const m = ICON_MAP[n?.icon] || { icon: Bell, color: "text-slate-400", bg: "bg-slate-700/40" };
                  const I = m.icon;
                  return (
                    <div
                      key={n?.id}
                      data-testid={`notification-${n?.id}`}
                      onClick={() => handleNotificationClick(n)}
                      className={`flex gap-3 px-4 py-3 border-b border-slate-800/60 hover:bg-slate-800/40 transition cursor-pointer ${
                        !n.read ? "bg-slate-800/20" : ""
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full ${m.bg} flex items-center justify-center shrink-0`}>
                        <I className={`w-4 h-4 ${m.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm font-semibold text-slate-100 truncate">{n?.title}</div>
                          {!n?.read && (
                            <span className="w-2 h-2 rounded-full bg-[#25D366] mt-1.5 shrink-0" />
                          )}
                        </div>
                        {n?.body && <div className="text-xs text-slate-400 line-clamp-2 mt-0.5">{n?.body}</div>}
                        <div className="text-[10px] text-slate-600 mt-1">{formatTime(n?.created_at)}</div>
                      </div>
                    </div>
                  );
                })
              )}

              {hasMore && (
                <div className="p-3">
                  <button
                    type="button"
                    onClick={() => fetchNotifications({ tab: activeTab, append: true })}
                    disabled={loading}
                    className="w-full rounded-lg border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Carregando..." : "Carregar mais"}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}